import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import './TemplateLibraryModal.css';

function TemplateLibraryModal({ isOpen, onClose, onInsert }) {
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const categories = [
        { value: 'all', label: 'Tous' },
        { value: 'banner', label: 'Bannières' },
        { value: 'footer', label: 'Footers' },
        { value: 'social', label: 'Réseaux sociaux' },
        { value: 'disclaimer', label: 'Disclaimers' },
        { value: 'custom', label: 'Personnalisés' }
    ];

    useEffect(() => {
        if (isOpen) {
            loadTemplates();
        }
    }, [isOpen]);

    useEffect(() => {
        filterTemplates();
    }, [templates, searchTerm, selectedCategory]);

    const loadTemplates = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await API.getTemplates();
            if (response && response.success) {
                setTemplates(response.data.templates);
            }
        } catch (err) {
            setError('Erreur lors du chargement des modèles');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterTemplates = () => {
        let filtered = [...templates];

        // Filtre par recherche
        if (searchTerm) {
            filtered = filtered.filter(tpl =>
                tpl.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (tpl.description && tpl.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Filtre par catégorie
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(tpl => tpl.category === selectedCategory);
        }

        setFilteredTemplates(filtered);
    };

    const handleInsert = () => {
        if (selectedTemplate) {
            onInsert(selectedTemplate.id);
            handleClose();
        }
    };

    const handleClose = () => {
        setSelectedTemplate(null);
        setSearchTerm('');
        setSelectedCategory('all');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="template-library-modal-overlay" onClick={handleClose}>
            <div className="template-library-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="template-library-modal-header">
                    <h2>Sélectionner un modèle</h2>
                    <button className="close-btn" onClick={handleClose}>×</button>
                </div>

                <div className="template-library-modal-toolbar">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Rechercher un modèle..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="category-filters">
                        {categories.map(cat => (
                            <button
                                key={cat.value}
                                className={`category-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(cat.value)}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="template-library-modal-body">
                    {loading && (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Chargement des modèles...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {!loading && filteredTemplates.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-icon">📄</div>
                            <p>Aucun modèle trouvé</p>
                        </div>
                    )}

                    {!loading && filteredTemplates.length > 0 && (
                        <div className="templates-list">
                            {filteredTemplates.map(template => (
                                <div
                                    key={template.id}
                                    className={`template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedTemplate(template)}
                                >
                                    <div className="template-item-header">
                                        <div className="template-category-badge">
                                            {categories.find(c => c.value === template.category)?.label || template.category}
                                        </div>
                                        {template.is_public && (
                                            <div className="template-public-badge">Public</div>
                                        )}
                                    </div>
                                    <h3 className="template-item-name">{template.name}</h3>
                                    {template.description && (
                                        <p className="template-item-description">{template.description}</p>
                                    )}
                                    <div className="template-item-meta">
                                        <span>Créé le {new Date(template.created_at).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="template-library-modal-footer">
                    <button className="btn-secondary" onClick={handleClose}>
                        Annuler
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleInsert}
                        disabled={!selectedTemplate}
                    >
                        Insérer
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TemplateLibraryModal;
